/**
 * acmConjunto.ts — conjuntos de porta: simples, dupla (2 folhas), folha + bandeira e folha + fixo lateral.
 *
 * Cada folha é calculada pelo motor de uma folha só (`calcularPorta`), com as medidas dadas e sem marco; o marco
 * (batentes, verga e, conforme a variação, travessa ou trave central) é calculado uma vez para o vão inteiro.
 * As medidas de folga entre peças e do painel fixo são derivadas dos descontos da linha e ainda não foram validadas
 * pelo Método ACM: o conjunto avisa "fora do padrão".
 */
import {
    CalcularPortaParams, CAMPOS_DA_FOLHA, DetalhesFolha, MODELOS, calcularPorta, marcoDaPorta, nomeComercial, normalizarParams, planoBarras, REGRAS,
} from './calculoAcm';

export type Variacao = 'simples' | 'dupla' | 'bandeira' | 'fixo-lateral';

export const CONJUNTO = {
    /** Folga entre as duas folhas da dupla. */
    FOLGA_CENTRAL: 6,
    /** Folga entre o painel fixo e o marco. */
    FOLGA_PAINEL: 2,
    BANDEIRA_PADRAO: 600, BANDEIRA_MIN: 300,
    FIXO_PADRAO: 400, FIXO_MIN: 200,
    PAINEL_MIN: 100,
};

export const VARIACOES: Array<{ id: Variacao; nome: string }> = [
    { id: 'simples', nome: 'Simples (uma folha)' },
    { id: 'dupla', nome: 'Dupla (duas folhas)' },
    { id: 'bandeira', nome: 'Folha + bandeira' },
    { id: 'fixo-lateral', nome: 'Folha + fixo lateral' },
];

export interface DescFolha {
    id: 'f1' | 'f2' | 'fixo';
    papel: 'principal' | 'segunda' | 'fixa';
    rotulo: string;
    /** Posição do canto inferior esquerdo da folha no vão (mm, origem no canto inferior esquerdo do vão). */
    ox: number;
    oy: number;
    largura: number;
    altura: number;
    /** Desenhada espelhada (puxador do outro lado); o cálculo é sempre na orientação nativa. */
    espelhada: boolean;
}

export interface FolhaConjunto extends DescFolha {
    params: CalcularPortaParams;
    d: any;
}

/** Variação em vigor (a de correr só existe simples). */
export function variacaoDe(p: CalcularPortaParams): Variacao {
    if (p.modelo === 'correr') return 'simples';
    return p.variacao === 'dupla' || p.variacao === 'bandeira' || p.variacao === 'fixo-lateral' ? p.variacao : 'simples';
}

const limitar = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);
const nOu = (v: unknown, def: number) => (v == null || v === '' || !Number.isFinite(Number(v)) ? def : Number(v));

/** Região de cada folha no vão, a partir da variação e das medidas do vão. */
export function descreverFolhas(entrada: CalcularPortaParams): DescFolha[] {
    const p = normalizarParams(entrada);
    const m = MODELOS[p.modelo];
    const VL = Number(p.vaoLargura) || 0, VA = Number(p.vaoAltura) || 0;
    const FA0 = VA - m.descontoAltura;
    const v = variacaoDe(p);
    const F = CONJUNTO.FOLGA_PAINEL;
    const espelharPrincipal = v === 'dupla' ? true : !!p.espelhar;

    if (v === 'simples') {
        const FL = VL - m.descontoLargura;
        return [{ id: 'f1', papel: 'principal', rotulo: 'Folha', ox: (VL - FL) / 2, oy: m.folgaInf, largura: FL, altura: FA0, espelhada: espelharPrincipal }];
    }
    if (v === 'dupla') {
        const FL = (VL - m.descontoLargura - CONJUNTO.FOLGA_CENTRAL) / 2;
        const ox1 = m.descontoLargura / 2;
        return [
            { id: 'f1', papel: 'principal', rotulo: 'Folha 1', ox: ox1, oy: m.folgaInf, largura: FL, altura: FA0, espelhada: true },
            { id: 'f2', papel: 'segunda', rotulo: 'Folha 2', ox: ox1 + FL + CONJUNTO.FOLGA_CENTRAL, oy: m.folgaInf, largura: FL, altura: FA0, espelhada: false },
        ];
    }
    if (v === 'bandeira') {
        const bh = limitar(nOu(p.bandeiraAltura, CONJUNTO.BANDEIRA_PADRAO), CONJUNTO.BANDEIRA_MIN, Math.max(CONJUNTO.BANDEIRA_MIN, VA - 1200));
        const FL = VL - m.descontoLargura, FA = VA - bh - m.descontoAltura;
        const FLb = VL - 2 * m.btL - 2 * F;
        const FAb = bh - m.btT - m.btT - 2 * F; // transversal e verga do marco
        return [
            { id: 'f1', papel: 'principal', rotulo: 'Folha', ox: (VL - FL) / 2, oy: m.folgaInf, largura: FL, altura: FA, espelhada: espelharPrincipal },
            { id: 'fixo', papel: 'fixa', rotulo: 'Bandeira', ox: (VL - FLb) / 2, oy: VA - bh + m.btT + F, largura: FLb, altura: FAb, espelhada: false },
        ];
    }
    // fixo lateral
    const fl = limitar(nOu(p.fixoLargura, CONJUNTO.FIXO_PADRAO), CONJUNTO.FIXO_MIN, Math.max(CONJUNTO.FIXO_MIN, VL - 600));
    const lado = p.fixoLado === 'dir' ? 'dir' : 'esq';
    const vaoFolha = VL - fl - m.btL;
    const FL = vaoFolha - m.descontoLargura;
    const FLb = fl - m.btL - 2 * F, FAb = VA - m.btT - m.folgaInf - F;
    const oxFolha = lado === 'esq' ? fl + m.btL + m.descontoLargura / 2 : m.descontoLargura / 2;
    const oxFixo = lado === 'esq' ? m.btL + F : VL - fl + F;
    return [
        { id: 'f1', papel: 'principal', rotulo: 'Folha', ox: oxFolha, oy: m.folgaInf, largura: FL, altura: FA0, espelhada: espelharPrincipal },
        { id: 'fixo', papel: 'fixa', rotulo: 'Fixo lateral', ox: oxFixo, oy: m.folgaInf, largura: FLb, altura: FAb, espelhada: false },
    ];
}

/** Detalhes da folha `id`: a principal usa os do conjunto; a segunda copia a principal até ser editada; o fixo parte do zero. */
export function detalhesDaFolha(p: CalcularPortaParams, id: string): DetalhesFolha {
    const principal: Record<string, unknown> = {};
    CAMPOS_DA_FOLHA.forEach(c => { if ((p as any)[c] !== undefined) principal[c] = (p as any)[c]; });
    if (p.cores) principal.cores = p.cores;
    if (id === 'f1') return principal as DetalhesFolha;
    const propria = p.folhas?.[id];
    if (propria) return propria;
    return id === 'f2' ? (principal as DetalhesFolha) : {};
}

/**
 * Parâmetros do motor de uma folha só para a folha `id`. Na simples é o próprio `p`; no conjunto leva a medida da
 * folha dada, sem marco, e (no painel fixo) o papel 'fixa'.
 */
export function paramsDaFolha(entrada: CalcularPortaParams, id: string): CalcularPortaParams {
    const p = normalizarParams(entrada);
    if (variacaoDe(p) === 'simples') return p;
    const desc = descreverFolhas(p).find(f => f.id === id);
    if (!desc) return p;
    const det = detalhesDaFolha(p, id);
    // Conjunto: a folha só carrega os detalhes dela
    const base: CalcularPortaParams = { ...p };
    CAMPOS_DA_FOLHA.forEach(c => { delete (base as any)[c]; });
    delete base.folhas; delete base.variacao; delete base.bandeiraAltura; delete base.fixoLargura; delete base.fixoLado; delete base.espelhar;
    const cores = { ...(p.cores || {}), ...(id === 'f1' ? {} : (det.cores || {})) };
    const { cores: _c, ...resto } = det as any;
    return {
        ...base, ...resto, cores,
        folhaLargura: desc.largura, folhaAltura: desc.altura, semMarco: true,
        papel: desc.papel === 'fixa' ? 'fixa' : 'giro',
        // A cava só existe numa folha de giro da Social
        ...(desc.papel === 'fixa' ? { cava: false } : {}),
        ...(id === 'f2' && resto.cava === undefined ? { cava: p.cava } : {}),
    };
}

/** Grava de volta no conjunto os detalhes editados na folha `id` (devolve `pf` quando é a simples). */
export function aplicarNaFolha(p: CalcularPortaParams, id: string, pf: CalcularPortaParams): CalcularPortaParams {
    if (variacaoDe(p) === 'simples') return pf;
    const det: Record<string, unknown> = {};
    CAMPOS_DA_FOLHA.forEach(c => { if ((pf as any)[c] !== undefined) det[c] = (pf as any)[c]; });
    if (id === 'f1') {
        const novo: CalcularPortaParams = { ...p };
        CAMPOS_DA_FOLHA.forEach(c => { delete (novo as any)[c]; });
        return { ...novo, ...det, ...(pf.cores ? { cores: pf.cores } : {}) } as CalcularPortaParams;
    }
    return { ...p, folhas: { ...(p.folhas || {}), [id]: { ...det, ...(pf.cores ? { cores: pf.cores } : {}) } as DetalhesFolha } };
}

export interface Conjunto {
    variacao: Variacao;
    vao: { largura: number; altura: number };
    folhas: FolhaConjunto[];
    /** Marco do conjunto inteiro (nas variações com mais de uma região; na simples o marco vem na folha). */
    marco: { cortes: any[]; acm: any[]; travessa: { y0: number; y1: number } | null; trave: { x0: number; x1: number } | null } | null;
    pecasACM: any[];
    corteAluminio: any[];
    resumo: any;
    conferencias: any[];
    valido: boolean;
}

/** Calcula o conjunto (todas as folhas e o marco). */
export function calcularConjunto(entrada: CalcularPortaParams): Conjunto {
    const p = normalizarParams(entrada);
    const m = MODELOS[p.modelo];
    const VL = Number(p.vaoLargura) || 0, VA = Number(p.vaoAltura) || 0;
    const variacao = variacaoDe(p);
    const descs = descreverFolhas(p);
    const folhas: FolhaConjunto[] = descs.map(desc => {
        const params = paramsDaFolha(p, desc.id);
        return { ...desc, params, d: calcularPorta(params) };
    });

    let marco: Conjunto['marco'] = null;
    if (variacao !== 'simples') {
        const corU = (folhas[0].d.entrada.cores.U as string) || 'Preto';
        const base = marcoDaPorta(m, VL, VA, folhas[0].largura, corU);
        const cortes = [...base.cortes], acm = [...base.acm];
        const cB = m.perfilBatente.cod;
        const acmMarco = (pos: string, peca: string, qtd: number, larg: number, comp: number, obs: string) => acm.push({
            pos, peca, slot: 'U', cor: corU, corComercial: nomeComercial(corU), qtd, chapaLargura: larg, chapaComprimento: comp, obs, grupo: 'marco',
        });
        const larguraBarra = m.temCava ? 105 : 78;
        let travessa: { y0: number; y1: number } | null = null, trave: { x0: number; x1: number } | null = null;
        if (variacao === 'bandeira') {
            const fixo = folhas.find(f => f.id === 'fixo')!;
            cortes.push({ pos: 'T1', peca: 'Travessa do marco (sob a bandeira)', perfil: cB, qtd: 1, comprimento: VL - 2 * m.btL, grupo: 'marco' });
            acmMarco('BT5', 'Barra Chata — travessa do marco (as 2 faces)', 2, larguraBarra, VL - 2 * m.btL, 'BI: 2 lados');
            travessa = { y0: fixo.oy - CONJUNTO.FOLGA_PAINEL - m.btT, y1: fixo.oy - CONJUNTO.FOLGA_PAINEL };
        }
        if (variacao === 'fixo-lateral') {
            const fixo = folhas.find(f => f.id === 'fixo')!;
            const folha = folhas.find(f => f.id === 'f1')!;
            cortes.push({ pos: 'M1', peca: 'Trave central do marco (entre o fixo e a folha)', perfil: cB, qtd: 1, comprimento: VA - m.btT, grupo: 'marco' });
            acmMarco('BT6', 'Barra Chata — trave central do marco (as 2 faces)', 2, larguraBarra, VA - m.btT, 'BI: 2 lados');
            const lado = p.fixoLado === 'dir' ? 'dir' : 'esq';
            const x0 = lado === 'esq' ? fixo.ox + fixo.largura + CONJUNTO.FOLGA_PAINEL : folha.ox + folha.largura + m.descontoLargura / 2;
            trave = { x0, x1: x0 + m.btL };
        }
        marco = { cortes, acm, travessa, trave };
    }

    // ---- agregados (mesmo formato do resultado de uma folha só, para a ficha e os resumos)
    const cF = m.perfilFolha.cod, cB = m.perfilBatente.cod;
    const marcoPecas = variacao === 'simples' ? [] : marco!.acm;
    const pecasACM = [...folhas.flatMap(f => f.d.pecasACM.map((x: any) => ({ ...x, folha: f.id }))), ...marcoPecas.map((x: any) => ({ ...x, folha: 'marco' }))];
    const corteAluminio = [...folhas.flatMap(f => f.d.corteAluminio.map((x: any) => ({ ...x, folha: f.id }))), ...(marco?.cortes || []).map((x: any) => ({ ...x, folha: 'marco' }))];
    let linF = 0, linB = 0, areaFolha = 0;
    corteAluminio.forEach(c => {
        if (c.perfil === cF) linF += c.qtd * c.comprimento;
        if (c.perfil === cB) linB += c.qtd * c.comprimento;
    });
    pecasACM.forEach(x => { if (x.grupo === 'folha') areaFolha += x.qtd * x.chapaLargura * x.chapaComprimento / 1e6; });
    const planF = planoBarras(corteAluminio, cF), planB = planoBarras(corteAluminio, cB, REGRAS.KERF_BATENTE);
    const ferragens = folhas.map(f => f.d.resumo.ferragem).filter(f => f.quantidade > 0);
    const resumo = {
        linearFolha: linF, linearBatente: linB,
        barras: { [cF]: planF.length, [cB]: planB.length },
        pesoAluminio: linF / 1000 * m.perfilFolha.kgm + linB / 1000 * m.perfilBatente.kgm,
        areaACMFolha: areaFolha,
        pesoFolha: folhas.reduce((a, f) => a + f.d.resumo.pesoFolha, 0),
        // Cada folha de giro tem a sua ferragem; a carga mínima é a da folha mais pesada
        ferragem: ferragens.length === 0 ? folhas[0].d.resumo.ferragem
            : { tipo: ferragens[0].tipo, quantidade: ferragens.reduce((a, f) => a + f.quantidade, 0), cargaMinima: Math.max(...ferragens.map(f => f.cargaMinima)) },
        perimetroFolhasM: folhas.reduce((a, f) => a + 2 * (f.largura + f.altura) / 1000, 0),
    };

    const conferencias: any[] = [];
    folhas.forEach(f => f.d.conferencias.forEach((c: any) => conferencias.push(
        variacao === 'simples' ? c : { ...c, chave: `${f.id}:${c.chave}`, descricao: `[${f.rotulo}] ${c.descricao}` })));
    if (variacao !== 'simples') {
        const erro = (chave: string, descricao: string, ok: boolean) => conferencias.push({ chave, descricao, ok, severidade: 'erro' });
        erro('conjunto_folhas', 'Todas as folhas e painéis do conjunto têm medida positiva', folhas.every(f => f.largura > 0 && f.altura > 0));
        erro('conjunto_painel_min', `Painéis fixos com ao menos ${CONJUNTO.PAINEL_MIN} de largura e altura`,
            folhas.filter(f => f.papel === 'fixa').every(f => f.largura >= CONJUNTO.PAINEL_MIN && f.altura >= CONJUNTO.PAINEL_MIN));
        if (variacao === 'dupla') erro('conjunto_dupla_larga', 'Cada folha da dupla cabe na chapa comercial', folhas.every(f => f.d.valido || !f.d.conferencias.some((c: any) => c.chave === 'chapa_disponivel' && !c.ok)));
        conferencias.push({ chave: 'conjunto_padrao', severidade: 'padrao', ok: false,
            descricao: 'Conjunto (dupla, bandeira ou fixo lateral): folgas e medidas do painel derivadas dos descontos da linha, ainda não validadas pelo Método ACM' });
    }
    return { variacao, vao: { largura: VL, altura: VA }, folhas, marco, pecasACM, corteAluminio, resumo, conferencias, valido: conferencias.every(c => c.ok) };
}

/**
 * Resultado no formato de uma folha só, agregando o conjunto: serve à ficha técnica e aos resumos
 * (a folha de referência é a principal; peças, perfis e ferragens somam o conjunto todo).
 */
export function calcularPortaCompleta(entrada: CalcularPortaParams) {
    const c = calcularConjunto(entrada);
    if (c.variacao === 'simples') return c.folhas[0].d;
    const principal = c.folhas[0].d;
    return {
        ...principal,
        entrada: { ...principal.entrada, vaoLargura: c.vao.largura, vaoAltura: c.vao.altura },
        pecasACM: c.pecasACM, corteAluminio: c.corteAluminio, resumo: c.resumo,
        conferencias: c.conferencias, valido: c.valido, perimetroFolhasM: c.resumo.perimetroFolhasM,
    };
}

